import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { trackBrowsingActivity, type ActivityType } from '@/data/browsingActivity';

/**
 * Hook to automatically track browsing activity for authenticated users
 */
export function useBrowsingActivity() {
  const { user } = useAuth();

  const trackActivity = useCallback(
    async (productId: string, activityType: ActivityType, durationSeconds?: number) => {
      if (!user?.id) return;

      try {
        await trackBrowsingActivity({
          userId: user.id,
          productId,
          activityType,
          durationSeconds,
        });
      } catch (error) {
        // Silent fail - don't disrupt UX for analytics
        console.warn('Failed to track activity:', error);
      }
    },
    [user?.id]
  );

  return { trackActivity };
}

/**
 * Hook to track time spent viewing a product
 * Automatically tracks when user lands and leaves the page
 */
export function useProductViewTracking(productId: string | undefined) {
  const { user } = useAuth();
  const startTimeRef = useRef<number>();

  useEffect(() => {
    if (!user?.id || !productId) return;

    // Track view start
    startTimeRef.current = Date.now();

    return () => {
      // Track view end with duration
      if (startTimeRef.current) {
        const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
        
        // Only track if viewed for at least 3 seconds
        if (durationSeconds >= 3) {
          trackBrowsingActivity({
            userId: user.id,
            productId,
            activityType: 'view',
            durationSeconds,
          }).catch(() => {
            // Silent fail
          });
        }
      }
    };
  }, [user?.id, productId]);
}
